/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { useState, useEffect } from 'react';
import { findParticipantById } from '../services/RegistrationService';

/**
 * Custom hook to fetch and manage participant data based on participant ID.
 *
 * @param {string} participantId - The ID of the participant to fetch data for.
 * @returns {Array} An array containing the participant data and a loading state.
 */
export function useParticipantData(participantId) {
  // State variable to store participant data
  const [participant, setParticipant] = useState(null);
  // State variable to indicate loading status
  const [isLoadingParticipant, setLoadingParticipant] = useState(true);

  useEffect(() => {
    // Function to asynchronously fetch participant data
    const fetchParticipant = async () => {
      setLoadingParticipant(true);
      try {
        // Fetch participant data using the provided participantId
        const data = await findParticipantById(participantId);
        setParticipant(data);
      } catch (err) {
        console.error('Error fetching participant:', err);
      } finally {
        setLoadingParticipant(false);
      }
    };

    if (participantId) {
      fetchParticipant();
    } else {
      setLoadingParticipant(false);
    }
  }, [participantId]);

  return [participant, isLoadingParticipant];
}